import { createApi } from '@reduxjs/toolkit/query/react';
import qs from 'qs';

import { CompensationStatus, CompensationType, EmployeeStatus, HttpMethod, LegalEntity } from 'enums';
import { Payment, Period, ExportPaymentsBody } from 'types';

import { baseQuery } from './base-query';

export type { ExportPaymentsBody };

interface ODataResponse<T> {
  value: T;
}

interface ExportRequestsBody {
  employeeIds: number[];
  compensationRequestTypes: CompensationType[];
  periodIds: number[];
  legalEntities?: LegalEntity[];
}

export interface UpdateStatusBody {
  status: CompensationStatus;
  compensationRequestIdentifiers: number[];
}

export interface UpdateLegalEntityBody {
  targetPeriod: string;
}

export const apiService = createApi({
  baseQuery,
  tagTypes: ['Period', 'Requests'],
  refetchOnMountOrArgChange: 30,
  endpoints: (build) => ({
    fetchPeriods: build.query<Period[], void>({
      query: () => ({ url: `periods`, method: HttpMethod.GET }),
      providesTags: () => ['Period'],
    }),
    fetchRequests: build.query<
      number[],
      { periods: number[]; types: CompensationType[]; legalEntities: LegalEntity[] }
    >({
      query: ({ periods = [], types = [], legalEntities = [] }) => {
        const periodFilter = periods.length ? `periodId in (${periods.join(',')})` : '';
        const typeFilter = types.length ? `type in ('${types.join("','")}')` : '';
        const legalEntityFilter = legalEntities.length ? `legalEntity in ('${legalEntities.join("','")}')` : '';
        const employmentStatusFilter = `employmentStatus in ('${EmployeeStatus.Employee}','${EmployeeStatus.Evaluation}')`;
        const statusFilter = `status eq '${CompensationStatus.Approved}'`;

        const filters = [periodFilter, typeFilter, legalEntityFilter, employmentStatusFilter, statusFilter]
          .filter(Boolean)
          .join(' and ');

        return {
          url: `odata/requests?$filter=${filters}&$select=employeeID`,
          method: HttpMethod.GET,
        };
      },
      transformResponse: (response: ODataResponse<{ employeeID: number }[]>) => [
        ...new Set(response.value.map(({ employeeID }) => employeeID)),
      ],
      providesTags: () => ['Requests'],
    }),
    exportRequests: build.mutation<void, ExportRequestsBody>({
      query: (body) => ({
        url: `Management/exportedRequests`,
        method: HttpMethod.POST,
        body,
      }),
      invalidatesTags: ['Requests'],
    }),
    closePeriod: build.mutation<void, number>({
      query: (id) => ({
        url: `periods/${id}`,
        method: HttpMethod.PATCH,
        body: {
          isActive: false,
        },
      }),
      invalidatesTags: ['Period'],
    }),
    getExportRequests: build.mutation<Payment[], ExportPaymentsBody>({
      query: ({
        compensationRequestTypes,
        compensationRequestStatuses,
        periodIds,
        currentYear,
        legalEntities,
        employeeIds,
        employmentStatuses,
      }) => ({
        url: `Management/reports/calculatedPayments`,
        params: {
          compensationRequestTypes,
          compensationRequestStatuses,
          periodIds,
          currentYear,
          employeeIds,
          legalEntities,
          employmentStatuses,
        },
        paramsSerializer: (params: Partial<ExportPaymentsBody>) => {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
        method: HttpMethod.GET,
      }),
    }),
    updateStatus: build.mutation<void, UpdateStatusBody>({
      query: ({ status, compensationRequestIdentifiers }) => ({
        url: `Management/requests/batch/status?status=${status}`,
        method: HttpMethod.PATCH,
        body: { compensationRequestIdentifiers },
      }),
      invalidatesTags: ['Requests'],
    }),
    updateLegalEntity: build.mutation<void, UpdateLegalEntityBody>({
      query: ({ targetPeriod }) => ({
        url: `Management/requests/legal-entity`,
        method: HttpMethod.PATCH,
        body: { targetPeriod },
      }),
      invalidatesTags: ['Requests'],
    }),
    deleteRequest: build.mutation<void, number>({
      query: (id) => ({
        url: `Users/requests/${id}`,
        method: HttpMethod.DELETE,
      }),
      invalidatesTags: ['Requests'],
    }),
  }),
});

export const {
  useFetchPeriodsQuery,
  useFetchRequestsQuery,
  useClosePeriodMutation,
  useExportRequestsMutation,
  useGetExportRequestsMutation,
  useUpdateStatusMutation,
  useUpdateLegalEntityMutation,
  useDeleteRequestMutation,
} = apiService;
